import { InMemoryCache, makeVar } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';

export const selectedDivisionIdStateVar = makeVar<string | number | null>(null);

// Apollo throws if a keyField is absent from the selection set. Many Media
// writes (uploads, schedule attachments) omit `id` and only have `mediaId`.
// Return false in that case so the object stays embedded instead of crashing.
const mediaKeyFields = (object: { id?: unknown; transformation?: { startOffset?: string | null } | null }) => {
  if (object.id == null) {
    return false;
  }
  if (object.transformation?.startOffset) {
    return ['id', 'transformation', ['startOffset']];
  }
  return ['id'];
};

export const cacheConfig = {
  typePolicies: {
    Query: {
      fields: {
        selectedDivisionId: {
          read() {
            return selectedDivisionIdStateVar();
          },
        },
        getOrganizationRepresentativeLists: {
          merge: true,
        },
        getCommentsForPresentationSubmissionPublic: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        getPresentationFolders: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        getPresentationsByProject: {
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        searchProjectSeriesByOrg: {
          keyArgs: ['input'],
          merge(existing, incoming) {
            if (!existing) {
              return incoming;
            }

            return {
              ...existing,
              objects: [...(existing.objects ?? []), ...(incoming.objects ?? [])],
            };
          },
        },
        projectRoles: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        searchRepresentatives: {
          keyArgs: ['input'],
          merge(existing, incoming) {
            if (!existing) {
              return incoming;
            }

            return {
              ...existing,
              organizations: [...(existing.organizations ?? []), ...(incoming.organizations ?? [])],
            };
          },
        },
        scheduleResponses: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        scheduleResponsesCanceled: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        timeZones: {
          // Merge function for timeZones field
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            // Simply return the incoming data to replace existing data
            // Or use a more sophisticated merge strategy if needed
            return incoming;
          },
        },
        getCommentsForSubmission: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        mediaRequestResponseCount: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    AuditionGroup: {
      fields: {
        medias: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
        mediaCount: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    Media: {
      keyFields: mediaKeyFields,
      fields: {
        transformation: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    PresentationPublic: {
      fields: {
        folder: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    ScheduleDetail: {
      fields: {
        timeframesSummary: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    ScheduleTimeframeSummary: {
      fields: {
        rolesSummary: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    ScheduleSlot: {
      fields: {
        submissions: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    ScheduleTalentPool: {
      fields: {
        submissions: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing = [], incoming = []) {
            return incoming;
          },
        },
      },
    },
    SubmissionResponses: {
      keyFields: ['submissionId'],
    },
    SubmissionResponse: {
      keyFields: ['responseId'],
    },
    ChatTopic: {
      keyFields: ['topicKey'],
    },
    WfChatTopic: {
      keyFields: ['topicKey'],
    },
    // Size sheet metric objects reuse the same numeric `id` across different
    // attributes (e.g. waist + inseam both id:2 with different inch/cm). Apollo's
    // default typename+id normalization would collapse them into one entity and
    // overwrite values. Keep them embedded under the parent talent size sheet.
    PresentationSizeSheetsMetricsModel: {
      keyFields: false,
    },
    PresentationSizeSheetsSizeMetricsModel: {
      keyFields: false,
    },
    PresentationSizeSheetsProfileSizeModel: {
      keyFields: false,
    },
    PresentationSizeSheetsWeightMetricsModel: {
      keyFields: false,
    },
    // SubFieldsGql entities with conflicting ids are used across multiple
    // parents (e.g. shoe modifiers, size modifiers). Disable normalization
    // to keep them embedded and prevent cache collisions.
    SubFieldsGql: {
      keyFields: false,
    },
    // coverUrl is deprecated/optional. Include it only when present so the same
    // profile with different covers stays distinct, without requiring coverUrl
    // on every WfProfile selection set (Apollo throws if a keyField is missing).
    WfProfile: {
      keyFields: (object) => {
        if (object.id == null) {
          return false;
        }
        return object.coverUrl ? ['id', 'coverUrl'] : ['id'];
      },
    },
    ReportMediaModel: {
      keyFields: mediaKeyFields,
    },
    ScheduleTimeframeRoleSummary: {
      keyFields: (object) => {
        if (object.id == null || object.timeframeId == null) {
          return false;
        }
        return ['id', 'timeframeId'];
      },
    },
    PresentationFilterRole: {
      keyFields: (object) => {
        if (object.id) {
          return ['id'];
        }
        if (object.name) {
          return ['name'];
        }
        return false;
      },
    },
    CollaboratorInvite: {
      keyFields: ['inviteId'],
    },
    CollaboratorsInvite: {
      keyFields: ['inviteId'],
    },
    CollaboratorDetails: {
      keyFields: (object) => (object.collaboratorId != null ? ['collaboratorId'] : false),
    },
    CollaboratorInviteProjectDetails: {
      keyFields: (object) => (object.projectId != null ? ['projectId'] : false),
    },
  },
};

export const buildCache = () => {
  return new InMemoryCache(cacheConfig);
};

export default buildCache();

export interface MockCacheParams {
  selectedDivisionId?: string | number;
}

const mockCacheConfig = ({ selectedDivisionId = null }: MockCacheParams) => {
  const mockConfig = cloneDeep(cacheConfig);
  selectedDivisionIdStateVar(selectedDivisionId);

  (mockConfig.typePolicies.Query.fields as any).selectedDivisionId = {
    ...(mockConfig.typePolicies.Query.fields as any).selectedDivisionId,
    read() {
      return selectedDivisionIdStateVar();
    },
  };

  (mockConfig as any).typePolicies.Project = {
    merge: true,
  };

  (mockConfig as any).typePolicies.Role = {
    fields: {
      submissions: {
        merge(_existing = [], incoming = []) {
          return incoming;
        },
      },
      publicationSettings: {
        merge(_existing = [], incoming = []) {
          return incoming;
        },
      },
      sides: {
        merge(_existing = [], incoming = []) {
          return incoming;
        },
      },
      photos: {
        merge(_existing = [], incoming = []) {
          return incoming;
        },
      },
    },
  };

  return mockConfig;
};

export const mockCache = (params: MockCacheParams = {}) => {
  return new InMemoryCache(mockCacheConfig(params));
};
